/**
 * The page "Learn from sales" (20 Oct 2026): TanStack Query hooks over `/sales-truth/*`.
 *
 * - `GET /sales-truth/summary` (`getSalesTruthSummary`): the tab "Overview".
 * - `GET /sales-truth/inquiries` (`listSalesTruthInquiries`): the tab "Inquiries", one page.
 * - `GET /sales-truth/inquiries/{case_id}` (`getSalesTruthInquiry`): the drawer of one inquiry.
 * - `GET /sales-truth/people` (`listSalesTruthPeople`): the tab "Sales people".
 * - `GET /sales-truth/learnings` (`listTruthLearnings`): the tab "Learnings".
 * - `POST /sales-truth/learnings/{id}/approve` and `/retract`: a manager's decision
 *   (capabilities `see_sales_truth` and `approve_learned`).
 * - `POST /sales-truth/learnings/{id}/multi-account` (`setTruthLearningMultiAccount`): a
 *   manager sets or clears the multi-account mark of a customer learning's sender
 *   (decision D5, the same capabilities).
 *
 * Every route needs the capability `see_sales_truth` (managers and admins); the server
 * answers 403 without it and binds every row to the areas of the session. The first part
 * of each query key is the request key of the usage vocabulary (`sales-truth-*`).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveTruthLearning,
  getSalesTruthInquiry,
  getSalesTruthSummary,
  listSalesTruthInquiries,
  type ListSalesTruthInquiriesData,
  listSalesTruthPeople,
  listTruthLearnings,
  type ListTruthLearningsData,
  retractTruthLearning,
  type SalesTruthInquiryDetail,
  type SalesTruthInquiryList,
  type SalesTruthPeople,
  type SalesTruthSummary,
  setTruthLearningMultiAccount,
  type TruthLearning,
  type TruthLearningList,
} from "./generated";

export type {
  CaseGroundTruth,
  LineDiffCounts,
  LineDiffRow,
  OrderLine,
  SalesTruthFunnelRow,
  SalesTruthInquiry,
  SalesTruthInquiryDetail,
  SalesTruthInquiryList,
  SalesTruthKpis,
  SalesTruthPeople,
  SalesTruthPerson,
  SalesTruthSourceCount,
  SalesTruthSummary,
  TruthLearning,
  TruthLearningCounts,
  TruthLearningList,
  TruthLearnSwitchState,
} from "./generated";

/** The filters of the list (every one optional; the server has the defaults). */
export type SalesTruthQuery = NonNullable<ListSalesTruthInquiriesData["query"]>;
/** The filters of the summary and the people: the shared part of the list filters. */
export type SalesTruthSummaryQuery = Pick<SalesTruthQuery, "area" | "days" | "source" | "taker">;
/** The filters of the tab "Learnings". */
export type TruthLearningQuery = NonNullable<ListTruthLearningsData["query"]>;

// The query keys; the first part is the request key of the usage vocabulary
// (`tests/usage-capture.test.mjs` reads each named key).
export const salesTruthSummaryKey = (query: SalesTruthSummaryQuery) =>
  ["sales-truth-summary", query] as const;
export const salesTruthInquiriesKey = (query: SalesTruthQuery) =>
  ["sales-truth-inquiries", query] as const;
export const salesTruthInquiryKey = (caseId: string | null) =>
  ["sales-truth-inquiry", caseId] as const;
export const salesTruthPeopleKey = (query: SalesTruthSummaryQuery) =>
  ["sales-truth-people", query] as const;
export const truthLearningsKey = (query: TruthLearningQuery) =>
  ["sales-truth-learnings", query] as const;
export const TRUTH_LEARNING_WRITE_KEY = ["sales-truth-learning-write"] as const;
/** What a decision on a learning makes stale: every read of the page and of "/learned". */
const DECISION_STALE_KEY = [
  "sales-truth-summary",
  "sales-truth-inquiries",
  "sales-truth-inquiry",
  "sales-truth-people",
  "sales-truth-learnings",
  "learned-proposals",
  "learning-stats",
  // the mark of a sender shows in "Why this customer" on the case page (decision D5)
  "case-customer-memory",
] as const;

/** The reads change at most with each ingest run. */
const STALE_MS = 60_000;

/** The tab "Overview". */
export function useSalesTruthSummary(query: SalesTruthSummaryQuery, options = { enabled: true }) {
  return useQuery({
    queryKey: salesTruthSummaryKey(query),
    queryFn: async (): Promise<SalesTruthSummary | null> => {
      const { data } = await getSalesTruthSummary({ query, throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** The tab "Inquiries", one page. */
export function useSalesTruthInquiries(query: SalesTruthQuery, options = { enabled: true }) {
  return useQuery({
    queryKey: salesTruthInquiriesKey(query),
    queryFn: async (): Promise<SalesTruthInquiryList | null> => {
      const { data } = await listSalesTruthInquiries({ query, throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** The drawer of one inquiry; off while no inquiry is open. */
export function useSalesTruthInquiry(caseId: string | null) {
  return useQuery({
    queryKey: salesTruthInquiryKey(caseId),
    queryFn: async (): Promise<SalesTruthInquiryDetail | null> => {
      const { data } = await getSalesTruthInquiry({
        path: { case_id: caseId ?? "" },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: caseId !== null,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** The tab "Sales people". */
export function useSalesTruthPeople(query: SalesTruthSummaryQuery, options = { enabled: true }) {
  return useQuery({
    queryKey: salesTruthPeopleKey({ area: query.area, days: query.days }),
    queryFn: async (): Promise<SalesTruthPeople | null> => {
      const { data } = await listSalesTruthPeople({
        query: { area: query.area, days: query.days },
        throwOnError: true,
      });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** The tab "Learnings". */
export function useTruthLearnings(query: TruthLearningQuery, options = { enabled: true }) {
  return useQuery({
    queryKey: truthLearningsKey(query),
    queryFn: async (): Promise<TruthLearningList | null> => {
      const { data } = await listTruthLearnings({ query, throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** After a decision every read of the page and the learned corrections is read again. */
function useInvalidateAfterDecision() {
  const queryClient = useQueryClient();
  return () => {
    for (const head of DECISION_STALE_KEY) {
      void queryClient.invalidateQueries({ queryKey: [head] });
    }
  };
}

/** Approve one learning (`approve_learned`); the answer is the changed learning. */
export function useApproveTruthLearning() {
  const invalidate = useInvalidateAfterDecision();
  return useMutation({
    mutationKey: TRUTH_LEARNING_WRITE_KEY,
    mutationFn: async (learningId: number): Promise<TruthLearning> => {
      const { data } = await approveTruthLearning({
        path: { learning_id: learningId },
        throwOnError: true,
      });
      return data as TruthLearning;
    },
    onSuccess: invalidate,
  });
}

/** Retract one learning (`approve_learned`); the answer is the changed learning. */
export function useRetractTruthLearning() {
  const invalidate = useInvalidateAfterDecision();
  return useMutation({
    mutationKey: TRUTH_LEARNING_WRITE_KEY,
    mutationFn: async (learningId: number): Promise<TruthLearning> => {
      const { data } = await retractTruthLearning({
        path: { learning_id: learningId },
        throwOnError: true,
      });
      return data as TruthLearning;
    },
    onSuccess: invalidate,
  });
}

/**
 * Set (`marked` true) or clear the multi-account mark of a customer learning's sender
 * (decision D5, `approve_learned`); the answer is the learning with `sender_multi_account`.
 */
export function useSetTruthLearningMultiAccount() {
  const invalidate = useInvalidateAfterDecision();
  return useMutation({
    mutationKey: TRUTH_LEARNING_WRITE_KEY,
    mutationFn: async (input: { learningId: number; marked: boolean }): Promise<TruthLearning> => {
      const { data } = await setTruthLearningMultiAccount({
        path: { learning_id: input.learningId },
        body: { marked: input.marked },
        throwOnError: true,
      });
      return data as TruthLearning;
    },
    onSuccess: invalidate,
  });
}
